import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * useAutoRedirect
 * - Starts a countdown and navigates after `delaySec` to `to`.
 * - Returns { secondsLeft, start } so you can trigger it on success.
 */
export function useAutoRedirect(to: string, delaySec = 5) {
  const nav = useNavigate();
  const [secondsLeft, setSecondsLeft] = useState<number>(delaySec);
  const timerRef = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  const start = useCallback(() => {
    clearTimer();
    setSecondsLeft(delaySec);
    timerRef.current = window.setInterval(() => {
      setSecondsLeft((s) => {
        const next = s - 1;
        if (next <= 0) {
          clearTimer();
          nav(to, { replace: true });
          return 0;
        }
        return next;
      });
    }, 1000);
  }, [clearTimer, delaySec, nav, to]);

  useEffect(() => () => clearTimer(), [clearTimer]);

  return { secondsLeft, start } as const;
}
